import {
  CargoPackageParentType,
  Commodity,
  RegistryTypes,
  PortTypes,
  SelectOption,
  DomesticTypes,
  BookingTemplateTypes,
  CommonTypes,
} from "@assets/types";

import { translate } from "./translateUtilities";

const getValueLabelObject = <T extends string | number>(
  value: T
): SelectOption<T> => {
  return {
    value: value,
    label: value.toString(),
  };
};
/**
 * Maps a list of values into a SelectOption list.
 * Mostly used to set the options of a select.
 * @param values The array of values that need to be transformed
 * @returns The array of SelectOptions
 */
export const listToSelectOptionMapper = <T extends string | number>(
  values: T[]
): SelectOption<T>[] => {
  return values.map((val) => {
    return getValueLabelObject(val);
  });
};

/**
 * A mapper to map a string or a number into a SelectOption.
 * Mostly used when setting the value of the select.
 * @param value The value to be mapped into a select option
 * @returns A select option object.
 */
export const singleValueToSelectOptionMapper = <T extends string | number>(
  value: T | null
) => {
  if (!value) {
    return null;
  }
  return getValueLabelObject(value);
};

export const customerCompanyValueMapper = (
  customer: DomesticTypes.DomesticCustomer
) => customer.No;

export const customerCompanyLabelMapper = (
  customer: DomesticTypes.DomesticCustomer
) => `${customer.Name}, ${customer.No}`;

export const departmentValueMapper = (
  department: DomesticTypes.DomesticDepartment
) => department.AddressNo;

export const departmentLabelMapper = (
  department: DomesticTypes.DomesticDepartment
) =>
  `${department.Name} ${department.Address}, ${department.PostCode}, ${department.City}`;

export const postCodeValueMapper = (postCode: CommonTypes.PostCode) =>
  postCode.PostCode;

export const postCodeLabelMapper = (postCode: CommonTypes.PostCode) =>
  `${postCode.PostCode} ${postCode.City}`;

export const portLabelMapper = (port: PortTypes.Port) =>
  `${port.FullName}, ${port.Country} (${port.PointCode})`;

export const portValueMapper = (port: PortTypes.Port) => port.PointCode;

export const cityValueMapper = (port: PortTypes.Port) => port.PointCode;

export const cityLabelMapper = (port: PortTypes.Port) =>
  port.FullName === port.LocalName
    ? `${port.FullName}`
    : `${port.FullName}, ${port.LocalName}`;

export const cityCountryLabelMapper = (port: PortTypes.Port) =>
  `${port.FullName}, ${port.Country} (${port.CountryCode})`;

export const commodityLabelMapper = (commodity: Commodity) =>
  `(${commodity.CommodityCode}) ${commodity.Description}`;

export const commodityValueMapper = (commodity: Commodity) =>
  commodity.CommodityCode;

export const packageTypeLabelMapper = (packageType: CargoPackageParentType) =>
  packageType.Description;

export const packageTypeValueMapper = (packageType: CargoPackageParentType) =>
  packageType.PackageCode;

export const incotermLabelMapper = (incoterm: RegistryTypes.Incoterm) =>
  `${incoterm.TermsCode} - ${incoterm.Description}`;

export const incotermValueMapper = (incoterm: RegistryTypes.Incoterm) =>
  incoterm.TermsCode;

export const hazardousMaterialValueMapper = (
  hazardousMaterial: DomesticTypes.DomesticHazardousMaterial
) => hazardousMaterial.Code;
export const hazardousMaterialLabelMapper = (
  hazardousMaterial: DomesticTypes.DomesticHazardousMaterial
) => `${hazardousMaterial.Code} | ${hazardousMaterial.TechnicalDescription}`;

export const itemConditionValueMapper = (
  itemCondition: DomesticTypes.DomesticItemCondition
) => itemCondition.Code;

export const itemConditionLabelMapper = (
  itemCondition: DomesticTypes.DomesticItemCondition
) => translate(`LABEL_ITEM_CONDITIONS_${itemCondition.Code}`);

export const partnerAddressLabelMapper = (
  partner: BookingTemplateTypes.Partner
) => partner.CompanyName;

export const partnerAddressValueMapper = (
  partner: BookingTemplateTypes.Partner
) => partner.PartnerCode;

export const partnerSavedAddressValueMapper = (
  partner: BookingTemplateTypes.Partner
) => `${partner.PartnerCode}-${partner.StreetAndNumber}-${partner.PostalCode}`;

export const partnerSavedAddressHeaderLabelMapper = (
  partner: BookingTemplateTypes.Partner
) => {
  return {
    header: partnerAddressLabelMapper(partner),
    subheader: `${partner.StreetAndNumber}, ${partner.City}, Iceland`,
  };
};

export const partnerAddressHeaderLabelMapper = (
  partner: BookingTemplateTypes.Partner
) => {
  return {
    header: partnerAddressLabelMapper(partner),
    subheader: `${partner.StreetAndNumber}, ${partner.PostalCode}, ${partner.Country}`,
  };
};

export const partnerLabelMapper = (partner: BookingTemplateTypes.Partner) =>
  partner.CompanyName ?? "";

export const countryValueMapper = (country: CommonTypes.Country) =>
  country.CountryCode;

export const countryLabelMapper = (country: CommonTypes.Country) =>
  `${country.FullName.toUpperCase()}. (${country.CountryCode})`;

export const hsCodesValueMapper = (code: CommonTypes.HSCode) => code.HSCode;
export const hsCodesLabelMapper = (code: CommonTypes.HSCode) =>
  `[${code.HSCode}] ${code.Description}`;
